import {
  type PlatformRole,
  type UserStatus,
  type DocumentArtifactStatus,
  type DocumentReleaseStatus,
} from "../config/types";

export const sessionStatusParam = "session";
export const sessionStatusExpired = "expired";

export class FetchError extends Error {
  code: string;
  details?: unknown;

  constructor(code: string, message?: string, details?: unknown) {
    super(message);
    this.code = code;
    this.details = details;
  }
}

export interface ApiError {
  code: string;
  details?: Record<string, unknown>;
  message?: string;
}

export type ScheduleReviewPeriod =
  "3 MONTH" | "6 MONTH" | "1 YEAR" | "3 YEAR" | "5 YEAR" | null;

export interface User {
  id: number;
  email: string;
  firstName: string;
  lastName?: string;
  imageUrl?: string;
  imageEtag?: string;
  position?: string | null;
  role: PlatformRole;
  status: UserStatus;
  categories: {
    id: number;
    title: string;
  }[];
}

export interface CurrentUser extends User {
  isSsoEnabled: boolean;
  isTwoFaEnabled: boolean;
  isUsingSpe: boolean;
}

export interface SsoDetails {
  redirectUrl: string;
}

export interface CreateSessionResponse {
  user: CurrentUser;
  ssoActivated: boolean;
  ssoAvailable: boolean;
}

export interface TQRcode {
  secret: string;
  otpauthUrl: string;
}

export interface OfficeData {
  title: string;
  accessToken: string;
  accessTokenTtl: string;
  officeActionUrl: string;
  faviconUrl: string;
  userId: string;
  ownerId: string;
  size: number;
  fileType: FileType;
  isReadOnly: boolean;
}

export type FileType =
  "docx" | "xlsx" | "pptx" | "wopitest" | "wopitestx" | "pdf";

export interface ReleaseSummaryWorkflow {
  isOngoing: boolean;
  steps: WorkflowStep[];
  totalSteps: number;
  completedSteps: number;
  workflowTemplateId: number | null;
  workflowTemplateName: string | null;
  description: string | null;
}

interface ReleaseSummaryArtifact {
  id: number;
  title: string;
  status: DocumentArtifactStatus;
  fileType: FileType;
  fileUpdatedAt: string | null;
  publishedAt: string | null;
  archivedAt: string | null;
  reviewFrom: string | null;
  reviewTo: string | null;
  permissions: ReleaseSummaryPermissions;
  workflow?: ReleaseSummaryWorkflow;
}

export interface DocumentReleaseSummary {
  id: number;
  majorVersion: number;
  minorVersion: number;
  status: DocumentReleaseStatus;
  adoptedAt: string | null;
  createdAt: string;
  artifact: ReleaseSummaryArtifact;
  readers: ReaderCountSummary;
  document: DocumentReleaseSummaryDocument;
}

export type DocumentReleaseSummaryWithWorkflow = DocumentReleaseSummary & {
  artifact: ReleaseSummaryArtifact & { workflow: ReleaseSummaryWorkflow };
};

export const hasWorkflowData = (
  release: DocumentReleaseSummary,
): release is DocumentReleaseSummaryWithWorkflow =>
  release.artifact.workflow !== undefined;

interface DocumentReleaseSummaryDocument {
  id: number;
  name: string;
  referenceNumber: string | null;
  tier: TierSummary | null;
  category: IdAndTitle;
  owner: UserSummary;
  publisher: UserSummary | null;
  tagIds: number[];
  scheduleReviewPeriod: ScheduleReviewPeriod;
  chartOrder: number | null;
}

interface ReaderCountSummary {
  totalCount: number;
  openedCount: number;
  readCount: number;
  currentUser?: {
    hasOpened: boolean;
    hasAccepted: boolean;
  };
}

interface ReleaseDetailsArtifact {
  id: number;
  title: string;
  status: DocumentArtifactStatus;
  description: string | null;
  fileId: string;
  fileType: FileType;
  fileUpdatedAt: string | null;
  publishedAt: string | null;
  archivedAt: string | null;
  reviewFrom: string | null;
  reviewTo: string | null;
  createdBy: UserSummary;
  isOngoing: boolean;
  workflowTemplateId: number | null;
  workflowTemplateName: string | null;
  steps: WorkflowStep[];
  stepInstances: WorkflowStepInstance[];
  permissions: ArtifactPermissions;
}

export interface DocumentReleaseDetails {
  id: number;
  majorVersion: number;
  minorVersion: number;
  status: DocumentReleaseStatus;
  adoptedAt: string | null;
  createdAt: string;
  artifact: ReleaseDetailsArtifact;
  readers: ReaderSummary[];
  document: {
    id: number;
    referenceNumber: string | null;
    tier: TierSummary | null;
    category: IdAndTitle;
    owner: UserSummary;
    tags: IdAndTitle[];
    releases: SiblingRelease[];
    scheduleReviewPeriod: ScheduleReviewPeriod;
  };
}

export interface IdAndTitle {
  id: number;
  title: string;
}

export interface LinkedDocument {
  id: number;
  title: string;
  tierId: number | null;
}

export interface TagWithDocuments extends IdAndTitle {
  documents?: {
    id: number;
    releases: {
      id: number;
      status: DocumentReleaseStatus;
      artifact: {
        id: number;
        title: string;
        status: DocumentArtifactStatus;
      };
    }[];
    archivedAt: string | null;
  }[];
}

interface TierSummary extends IdAndTitle {
  parentId: number | null;
}

export interface TierDetails extends TierSummary {
  description: string;
}

export interface CreateTierData {
  title: string;
  description?: string;
  parentId?: number | null;
}

export interface UpdateTierData {
  title?: string;
  description?: string;
}

interface SiblingRelease {
  id: number;
  majorVersion: number;
  status: DocumentReleaseStatus;
  artifact: {
    id: number;
    title: string;
    status: DocumentArtifactStatus;
    publishedAt: string | null;
  };
}

export interface ReaderSummary {
  userId: number;
  email: string;
  firstName: string;
  lastName?: string;
  hasOpened?: boolean;
  hasRead?: boolean;
}

export interface UserSummary {
  userId: number;
  firstName: string;
  lastName?: string;
  email: string;
  imageUrl?: string;
  hasOpened?: boolean;
}

export interface CreateUserData {
  email: string;
  position?: string | null;
  role: string;
  firstName: string;
  lastName: string;
  categoryIds?: (number | string)[];
}

export interface UpdateUserData extends Partial<CreateUserData> {
  file?: File;
  status?: UserStatus;
}

export const WorkflowStepUserPermissions = {
  read: "read",
  write: "write",
} as const;

export type WorkflowStepUserPermission =
  (typeof WorkflowStepUserPermissions)[keyof typeof WorkflowStepUserPermissions];

export const WorkflowStepUserResponses = {
  pending: "pending",
  approved: "approved",
  rejected: "rejected",
} as const;

export type WorkflowStepUserResponse =
  (typeof WorkflowStepUserResponses)[keyof typeof WorkflowStepUserResponses];

export const WorkflowStepInstanceCommentTypes = {
  free: "free",
  approved: "approved",
  rejected: "rejected",
} as const;

export type WorkflowStepInstanceCommentType =
  (typeof WorkflowStepInstanceCommentTypes)[keyof typeof WorkflowStepInstanceCommentTypes];

export interface WorkflowStepUser {
  userId: number;
  firstName: string;
  lastName?: string;
  imageUrl?: string;
  role?: PlatformRole;
  permission: WorkflowStepUserPermission;
  responseStatus: WorkflowStepUserResponse;
}

type WorkflowTemplateStepUser = Pick<WorkflowStepUser, "userId" | "permission">;

export const WorkflowStepStatuses = {
  completed: "completed",
  active: "active",
  upcoming: "upcoming",
} as const;
export type WorkflowStepStatus =
  (typeof WorkflowStepStatuses)[keyof typeof WorkflowStepStatuses];

export type WorkflowStepType = "review" | "approval";

interface WorkflowStepBase {
  id: number;
  name: string;
  type: WorkflowStepType;
  order: number;
}

export interface WorkflowStep extends WorkflowStepBase {
  description: string;
  dueAt: Date | null;
  status: WorkflowStepStatus;
  users: WorkflowStepUser[];
}

interface WorkflowTemplateStep extends WorkflowStepBase {
  description: string | null;
  dueAt: string | null;
  users: WorkflowTemplateStepUser[];
}

export interface WorkflowStepInstanceComment {
  id: number;
  createdBy: UserSummary;
  createdAt: string;
  type: WorkflowStepInstanceCommentType;
  text: string | null;
}

export interface WorkflowStepInstance {
  id: number;
  stepId: number;
  createdBy: UserSummary;
  createdAt: string;
  completedAt: string | null;
  isLatestStepInstance: boolean;
  comments: WorkflowStepInstanceComment[];
}

export type WorkflowStepInput = Pick<
  WorkflowStep,
  "name" | "description" | "type" | "order" | "status" | "users" | "dueAt"
> & {
  id?: number; // New steps don't have an ID
  _key: string; // Client-side stable key for React reconciliation
};

export type OfficeFileType = Exclude<
  FileType,
  "wopitest" | "wopitestx" | "pdf"
>;

export type OfficeRelease = DocumentDetailsRelease & {
  artifact: DocumentDetailsReleaseArtifact & { fileType: OfficeFileType };
};

export const isOfficeRelease = (
  release: DocumentDetailsRelease,
): release is OfficeRelease => {
  return ["docx", "xlsx", "pptx"].includes(release.artifact.fileType);
};

export interface ArtifactPermissions {
  canRead: boolean;
  canWrite: boolean;
  canManage: boolean;
  canPublish: boolean;
}

type ReleaseSummaryPermissions = Pick<
  ArtifactPermissions,
  "canManage" | "canPublish"
>;

interface DocumentDetailsReleaseArtifact {
  id: number;
  title: string;
  status: DocumentArtifactStatus;
  fileId: string;
  fileType: FileType;
  reviewFrom: string | null;
  reviewTo: string | null;
  fileUpdatedAt: string | null;
  isWorkflowOngoing: boolean;
  publishedAt: string | null;
  archivedAt: string | null;
  lockedBy: UserSummary | null;
  permissions: ArtifactPermissions;
}

export interface DocumentDetailsRelease {
  id: number;
  majorVersion: number;
  minorVersion: number;
  status: DocumentReleaseStatus;
  adoptedAt: string | null;
  createdAt: string;
  artifact: DocumentDetailsReleaseArtifact;
}

export interface DocumentDetails {
  id: number;
  name: string;
  projectId: string;
  referenceNumber: string | null;
  tier: IdAndTitle | null;
  category: IdAndTitle;
  owner: UserSummary;
  publisher: UserSummary | null;
  tags: IdAndTitle[];
  linkedDocuments: LinkedDocument[];
  releases: DocumentDetailsRelease[];
  scheduleReviewPeriod: ScheduleReviewPeriod;
  archivedAt: string | null;
}

export interface DocumentSummaryRelease {
  id: number;
  majorVersion: number;
  minorVersion: number;
  status: DocumentReleaseStatus;
  adoptedAt: string | null;
  createdAt: string;
  artifact: {
    id: number;
    title: string;
    status: DocumentArtifactStatus;
    fileType: FileType;
    isWorkflowOngoing: boolean;
    reviewFrom: string | null;
    reviewTo: string | null;
    fileUpdatedAt: string | null;
    publishedAt: string | null;
    archivedAt: string | null;
  };
}

export interface DocumentSummary {
  id: number;
  name: string;
  projectId: string;
  referenceNumber: string | null;
  tier: IdAndTitle | null;
  category: IdAndTitle;
  owner: UserSummary;
  publisher: UserSummary | null;
  tags: IdAndTitle[];
  parentDocumentIds: number[];
  childDocumentIds: number[];
  linkedDocumentIds: number[];
  chartOrder: number | null;
  scheduleReviewPeriod: ScheduleReviewPeriod;
  archivedAt: string | null;
  latestRelease: DocumentSummaryRelease;
  latestPublishedRelease: DocumentSummaryRelease | null;
  pin?: {
    description: string | null;
    createdAt: string;
  };
}

export interface DocumentHistoryEvent {
  timestamp: string;
  type: DocumentEventType;
  details?: Record<string, unknown>;
  release?: {
    id: number;
    order: number;
  };
  actor?: {
    userId: number;
    firstName: string;
    lastName: string;
    email?: string;
    imageUrl?: string;
  };
}

export interface DocumentHistoryResponse {
  history: DocumentHistoryEvent[];
}

type DocumentEventType =
  | "release_created"
  | "release_published"
  | "release_adopted"
  | "release_archived"
  | "document_user_added"
  | "document_user_removed"
  | "workflow_step_user_added"
  | "workflow_step_user_removed"
  | "workflow_started"
  | "workflow_transition"
  | "workflow_completed";

interface ReaderReportSummary {
  total: number;
  opened: number;
  read: number;
}

export interface ReaderReport {
  readers: ReaderSummary[];
  summary: ReaderReportSummary;
}
export interface CategoryData {
  title: string;
  description?: string | undefined;
}

export interface CategoryDetails {
  id: number;
  title: string;
  description: string;
  hasDocuments: boolean;
}

export interface CategoryType {
  title: string;
  description: string;
  id: number;
  order: number;
}

export const LanguageCodeMap = {
  BG: "BG",
  CS: "CS",
  DA: "DA",
  DE: "DE",
  EL: "EL",
  EN_GB: "en-GB",
  EN_US: "en-US",
  ES: "ES",
  ET: "ET",
  FI: "FI",
  FR: "FR",
  HU: "HU",
  ID: "ID",
  IT: "IT",
  JA: "JA",
  KO: "KO",
  LT: "LT",
  LV: "LV",
  NB: "NB",
  NL: "NL",
  PL: "PL",
  PT: "PT",
  RO: "RO",
  RU: "RU",
  SK: "SK",
  SL: "SL",
  SV: "SV",
  TR: "TR",
  UK: "UK",
  ZH: "ZH",
  AUTO_DETECT: "AUTO_DETECT",
} as const;

export type LanguageCode =
  (typeof LanguageCodeMap)[keyof typeof LanguageCodeMap];

export interface SpSite {
  id: string;
  displayName: string;
  webUrl: string;
}

export interface SpDrive {
  id: string;
  name: string;
  driveType: string;
  webUrl: string;
}

export interface VerifySpAccessResponse {
  ok: boolean;
}

interface SectionVersionArtifactUsage {
  id: number;
  documentId: number;
  releaseId: number;
  title: string;
  status: DocumentArtifactStatus;
}

export interface SectionVersion {
  id: number;
  htmlContent: string;
  xmlContent: string;
  createdAt: string;
  createdBy: UserSummary;
  usedInArtifacts: Array<SectionVersionArtifactUsage>;
}

export interface Section {
  id: number;
  name: string;
  sectionVersions: SectionVersion[];
}

export interface WorkflowTemplateListItem {
  id: number;
  isDefault: boolean;
  name: string | null;
  description: string | null;
  createdAt: string;
  updatedAt: string;
  stepCount: number;
}

export interface WorkflowTemplate {
  id: number;
  isDefault: boolean;
  name: string | null;
  description: string | null;
  createdAt: string;
  updatedAt: string;
  steps: WorkflowTemplateStep[];
}

export interface WorkflowTemplateData {
  name: string | null;
  description: string | null;
  steps: {
    id?: number;
    name: string;
    description: string | null;
    type: WorkflowStepType;
    dueAt: Date | null;
    users: WorkflowTemplateStepUser[];
  }[];
}

export interface ApplyDueDateRow {
  reviewFrom: string | null;
  reviewTo: string | null;
  stepDueDates: (string | null)[];
}

export interface ApplyWorkflowTemplateData {
  artifactIds: number[];
  dueDates?: Record<number, ApplyDueDateRow>;
  keepDescriptionArtifactIds?: number[];
}
